import { ElementType, useEffect, useRef, useState, createElement, useMemo, useCallback } from 'react';
import { gsap } from 'gsap';

interface TextTypeProps {
  className?: string;
  showCursor?: boolean;
  hideCursorWhileTyping?: boolean;
  cursorCharacter?: string | React.ReactNode;
  cursorBlinkDuration?: number;
  cursorClassName?: string;
  text: string | string[];
  as?: ElementType;
  typingSpeed?: number;
  initialDelay?: number;
  pauseDuration?: number;
  deletingSpeed?: number;
  loop?: boolean;
  textColors?: string[];
  variableSpeed?: { min: number; max: number };
  onSentenceComplete?: (sentence: string, index: number) => void;
  startOnVisible?: boolean;
  reverseMode?: boolean;
}

const TextType = ({
  text,
  as: Component = 'div',
  typingSpeed = 50,
  initialDelay = 0,
  pauseDuration = 2000,
  deletingSpeed = 30,
  loop = true,
  className = '',
  showCursor = true,
  hideCursorWhileTyping = false,
  cursorCharacter = '|',
  cursorClassName = '',
  cursorBlinkDuration = 0.5,
  textColors = [],
  variableSpeed,
  onSentenceComplete,
  startOnVisible = false,
  reverseMode = false,
  ...props
}: TextTypeProps & React.HTMLAttributes<HTMLElement>) => {
  const [displayedText, setDisplayedText] = useState('');
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const [isVisible, setIsVisible] = useState(!startOnVisible);
  const cursorRef = useRef<HTMLSpanElement>(null);
  const containerRef = useRef<HTMLElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const textArray = useMemo(() => (Array.isArray(text) ? text : [text]), [text]);

  const getSpeed = useCallback(() => {
    if (!variableSpeed) return typingSpeed;
    return Math.random() * (variableSpeed.max - variableSpeed.min) + variableSpeed.min;
  }, [variableSpeed, typingSpeed]);

  const getCurrentTextColor = () => {
    if (textColors.length === 0) return 'inherit';
    return textColors[currentTextIndex % textColors.length];
  };

  useEffect(() => {
    if (!startOnVisible || !containerRef.current) return;
    const observer = new IntersectionObserver(
      entries => { entries.forEach(e => { if (e.isIntersecting) setIsVisible(true); }); },
      { threshold: 0.1 }
    );
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [startOnVisible]);

  useEffect(() => {
    if (showCursor && cursorRef.current) {
      gsap.set(cursorRef.current, { opacity: 1 });
      gsap.to(cursorRef.current, {
        opacity: 0,
        duration: cursorBlinkDuration,
        repeat: -1,
        yoyo: true,
        ease: 'power2.inOut',
      });
    }
  }, [showCursor, cursorBlinkDuration]);

  useEffect(() => {
    if (!isVisible) return;

    const clear = () => { if (timeoutRef.current) clearTimeout(timeoutRef.current); };

    const runCycle = (textIdx: number) => {
      const raw = textArray[textIdx];
      const target = reverseMode ? raw.split('').reverse().join('') : raw;

      // Type out
      const typeChar = (i: number) => {
        if (i > target.length) {
          // Fully typed — pause then delete
          setIsTyping(false);
          timeoutRef.current = setTimeout(() => deleteChar(target.length), pauseDuration);
          return;
        }
        setDisplayedText(target.slice(0, i));
        setIsTyping(true);
        timeoutRef.current = setTimeout(() => typeChar(i + 1), variableSpeed ? getSpeed() : typingSpeed);
      };

      // Delete back
      const deleteChar = (i: number) => {
        if (i < 0) {
          // Fully deleted — move to next
          if (onSentenceComplete) onSentenceComplete(raw, textIdx);
          const nextIdx = (textIdx + 1) % textArray.length;
          if (!loop && nextIdx === 0) return;
          setCurrentTextIndex(nextIdx);
          timeoutRef.current = setTimeout(() => runCycle(nextIdx), 400);
          return;
        }
        setDisplayedText(target.slice(0, i));
        timeoutRef.current = setTimeout(() => deleteChar(i - 1), deletingSpeed);
      };

      timeoutRef.current = setTimeout(() => typeChar(0), textIdx === 0 ? initialDelay : 0);
    };

    runCycle(0);
    return clear;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  const shouldHideCursor = hideCursorWhileTyping && isTyping;

  return createElement(
    Component,
    { ref: containerRef, className: `inline-block whitespace-pre-wrap tracking-tight ${className}`, ...props },
    <span className="inline" style={{ color: getCurrentTextColor() || 'inherit' }}>
      {displayedText}
    </span>,
    showCursor && (
      <span
        ref={cursorRef}
        className={`ml-1 inline-block opacity-100 ${shouldHideCursor ? 'hidden' : ''} ${cursorClassName}`}
      >
        {cursorCharacter}
      </span>
    )
  );
};

export default TextType;
